/**
 * Client Search Utilities
 *
 * Advanced search functionality for clients including registration code pattern matching,
 * range filtering, and optimized search performance with caching.
 */

import { Client } from '@/types/contacts';

export interface ClientSearchOptions {
  query?: string;
  codeRange?: {
    start?: number;
    end?: number;
  };
  includeInactive?: boolean;
  searchFields?: Array<'name' | 'documentNumber' | 'email' | 'registrationCode' | 'phone' | 'city'>;
}

export interface ClientSearchResult {
  clients: Client[];
  totalCount: number;
  searchTime: number;
  queryType: 'text' | 'code' | 'range' | 'combined';
}

// Cache for search results
const searchCache = new Map<string, { result: ClientSearchResult; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Advanced search function for clients with registration code support
 */
export function searchClients(
  clients: Client[],
  options: ClientSearchOptions
): ClientSearchResult {
  const startTime = performance.now();

  // Generate cache key
  const cacheKey = generateCacheKey(clients, options);

  // Check cache first
  const cached = searchCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.result;
  }

  const {
    query = '',
    codeRange,
    includeInactive = false,
    searchFields = ['name', 'documentNumber', 'email', 'registrationCode', 'phone', 'city']
  } = options;

  let filteredClients = clients.filter(client =>
    includeInactive || client.isActive
  );

  let queryType: ClientSearchResult['queryType'] = 'text';

  // Handle different search patterns
  if (query.startsWith('#')) {
    // Registration code pattern search (#1234, #1000-2000)
    queryType = 'code';
    filteredClients = handleCodeSearch(filteredClients, query.substring(1));
  } else if (codeRange) {
    // Code range filtering
    queryType = 'range';
    filteredClients = handleCodeRange(filteredClients, codeRange);
  } else if (query.trim()) {
    // Text-based search
    queryType = 'text';
    filteredClients = handleTextSearch(filteredClients, query.trim(), searchFields);
  }

  const searchTime = performance.now() - startTime;
  const result: ClientSearchResult = {
    clients: filteredClients,
    totalCount: filteredClients.length,
    searchTime,
    queryType
  };

  // Cache the result
  searchCache.set(cacheKey, { result, timestamp: Date.now() });

  return result;
}

/**
 * Handle registration code pattern searches
 */
function handleCodeSearch(clients: Client[], codeQuery: string): Client[] {
  // Check if it's a range pattern (e.g., "1000-2000")
  if (codeQuery.includes('-')) {
    const [start, end] = codeQuery.split('-').map(s => s.trim());
    const startNum = parseInt(start, 10);
    const endNum = parseInt(end, 10);

    if (!isNaN(startNum) && !isNaN(endNum)) {
      return clients.filter(client => {
        if (!client.registrationCode) return false;
        const clientCode = parseInt(client.registrationCode, 10);
        return !isNaN(clientCode) && clientCode >= startNum && clientCode <= endNum;
      });
    }
  }

  // Check if it's a greater than pattern (e.g., ">1000")
  if (codeQuery.startsWith('>')) {
    const minCode = parseInt(codeQuery.substring(1), 10);
    if (!isNaN(minCode)) {
      return clients.filter(client => {
        if (!client.registrationCode) return false;
        const clientCode = parseInt(client.registrationCode, 10);
        return !isNaN(clientCode) && clientCode > minCode;
      });
    }
  }

  // Check if it's a less than pattern (e.g., "<2000")
  if (codeQuery.startsWith('<')) {
    const maxCode = parseInt(codeQuery.substring(1), 10);
    if (!isNaN(maxCode)) {
      return clients.filter(client => {
        if (!client.registrationCode) return false;
        const clientCode = parseInt(client.registrationCode, 10);
        return !isNaN(clientCode) && clientCode < maxCode;
      });
    }
  }

  // Exact code match
  const exactCode = parseInt(codeQuery, 10);
  if (!isNaN(exactCode)) {
    return clients.filter(client => {
      if (!client.registrationCode) return false;
      const clientCode = parseInt(client.registrationCode, 10);
      return !isNaN(clientCode) && clientCode === exactCode;
    });
  }

  // Partial code match (for typing autocomplete)
  const partialCode = parseInt(codeQuery, 10);
  if (!isNaN(partialCode)) {
    return clients.filter(client =>
      client.registrationCode &&
      client.registrationCode.toString().startsWith(codeQuery)
    );
  }

  return [];
}

/**
 * Handle code range filtering
 */
function handleCodeRange(clients: Client[], range: { start?: number; end?: number }): Client[] {
  const { start, end } = range;

  return clients.filter(client => {
    if (!client.registrationCode) return false;

    const clientCode = parseInt(client.registrationCode, 10);
    if (isNaN(clientCode)) return false;

    if (start !== undefined && clientCode < start) return false;
    if (end !== undefined && clientCode > end) return false;

    return true;
  });
}

/**
 * Handle text-based search across specified fields
 */
function handleTextSearch(
  clients: Client,
  query: string,
  searchFields: Array<'name' | 'documentNumber' | 'email' | 'registrationCode' | 'phone' | 'city'>
): Client[] {
  const normalizedQuery = normalizeText(query);

  return clients.filter(client => {
    return searchFields.some(field => {
      switch (field) {
        case 'name':
          return normalizeText(client.name).includes(normalizedQuery);
        case 'documentNumber':
          return normalizeText(client.documentNumber).includes(normalizedQuery);
        case 'email':
          return client.email && normalizeText(client.email).includes(normalizedQuery);
        case 'registrationCode':
          return client.registrationCode &&
                 normalizeText(client.registrationCode).includes(normalizedQuery);
        case 'phone':
          return client.phone && normalizeText(client.phone).includes(normalizedQuery);
        case 'city':
          return client.address?.city &&
                 normalizeText(client.address.city).includes(normalizedQuery);
        default:
          return false;
      }
    });
  });
}

/**
 * Normalize text for case-insensitive search with accent removal
 */
function normalizeText(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Generate cache key for search options
 */
function generateCacheKey(clients: Client[], options: ClientSearchOptions): string {
  const clientCount = clients.length;
  const optionsHash = JSON.stringify({
    query: options.query,
    codeRange: options.codeRange,
    includeInactive: options.includeInactive,
    searchFields: options.searchFields?.sort()
  });

  return `${clientCount}-${btoa(optionsHash)}`;
}

/**
 * Clear search cache
 */
export function clearClientSearchCache(): void {
  searchCache.clear();
}

/**
 * Get search suggestions for registration codes
 */
export function getCodeSuggestions(clients: Client[], partialCode: string): string[] {
  if (partialCode.length < 2) return [];

  const codes = clients
    .map(client => client.registrationCode)
    .filter((rc): rc is string => rc !== null && rc !== undefined)
    .filter(rc => rc.startsWith(partialCode))
    .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
    .slice(0, 10); // Limit to 10 suggestions

  return codes;
}

/**
 * Advanced search syntax parser
 */
export function parseAdvancedSearchSyntax(query: string): ClientSearchOptions {
  const options: ClientSearchOptions = {
    query: '',
    includeInactive: false,
    searchFields: ['name', 'documentNumber', 'email', 'registrationCode', 'phone', 'city']
  };

  // Parse field-specific searches (e.g., "name:João email:test@")
  const fieldMatches = query.match(/(\w+):([^\s]+)/g);
  if (fieldMatches) {
    const specificFields: Array<'name' | 'documentNumber' | 'email' | 'registrationCode' | 'phone' | 'city'> = [];

    fieldMatches.forEach(match => {
      const [field, value] = match.split(':');
      switch (field.toLowerCase()) {
        case 'name':
        case 'nome':
          specificFields.push('name');
          break;
        case 'document':
        case 'documento':
        case 'cpf':
        case 'cnpj':
          specificFields.push('documentNumber');
          break;
        case 'email':
          specificFields.push('email');
          break;
        case 'code':
        case 'codigo':
        case 'registration':
          specificFields.push('registrationCode');
          break;
        case 'phone':
        case 'telefone':
          specificFields.push('phone');
          break;
        case 'city':
        case 'cidade':
          specificFields.push('city');
          break;
      }
    });

    if (specificFields.length > 0) {
      options.searchFields = specificFields;
      // Remove field-specific parts from general query
      options.query = query.replace(/(\w+):([^\s]+)/g, '').trim();
    }
  } else {
    options.query = query;
  }

  // Parse inactive flag
  if (query.includes('inactive') || query.includes('inativo')) {
    options.includeInactive = true;
  }

  return options;
}

/**
 * Performance monitoring for search operations
 */
export interface SearchMetrics {
  averageSearchTime: number;
  totalSearches: number;
  cacheHitRate: number;
  mostCommonQueries: Array<{ query: string; count: number }>;
}

const searchMetrics = {
  totalSearches: 0,
  totalTime: 0,
  cacheHits: 0,
  queryCounts: new Map<string, number>()
};

export function getSearchMetrics(): SearchMetrics {
  return {
    averageSearchTime: searchMetrics.totalTime / Math.max(searchMetrics.totalSearches, 1),
    totalSearches: searchMetrics.totalSearches,
    cacheHitRate: searchMetrics.cacheHits / Math.max(searchMetrics.totalSearches, 1),
    mostCommonQueries: Array.from(searchMetrics.queryCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([query, count]) => ({ query, count }))
  };
}

export function resetSearchMetrics(): void {
  searchMetrics.totalSearches = 0;
  searchMetrics.totalTime = 0;
  searchMetrics.cacheHits = 0;
  searchMetrics.queryCounts.clear();
}

// Internal function to update metrics
function updateSearchMetrics(query: string, searchTime: number, wasCached: boolean): void {
  searchMetrics.totalSearches++;
  searchMetrics.totalTime += searchTime;

  if (wasCached) {
    searchMetrics.cacheHits++;
  }

  const count = searchMetrics.queryCounts.get(query) || 0;
  searchMetrics.queryCounts.set(query, count + 1);
}